import type { Composition } from '@medplum/fhirtypes'
import { defaultMerger } from '#src/merge/mergers/default-merger.ts'
import type { Merger, MergerArgs } from '#src/merge/mergers/merger.ts'
import { isOfType } from '#src/utils.ts'

/**
 * This merger is specifically designed for Composition resources. It handles the merging of Composition sections.
 *
 * Note: Elation visit note bullets, which map to Composition sections, do not have identifiers. This makes it impossible to uniquely identify them during the merging process.
 * Additionally, section categories can be reused in the Elation visit note, which maps to the Composition resource. This means that sections with the same category may be merged or replaced based on the merge strategy.
 *
 * The merge strategy implemented here is to replace the current sections with the incoming sections. This is because the lack of unique identifiers makes it difficult to implement a more sophisticated merge strategy.
 *
 * @param args - The merger arguments containing the current and incoming resources.
 * @param mergeFn - The merge function to use for merging other parts of the Composition resource.
 * @returns The merged Composition resource.
 */
export const compositionMerger: Merger = {
  canMerge: (args: MergerArgs): boolean =>
    isOfType(args.current, 'Composition'),

  merge: (args: MergerArgs, mergeFn): any => {
    const current = args.current as Composition
    const incoming = args.incoming as Composition

    const currentWithoutSection = { ...current }
    delete currentWithoutSection.section

    const merged = defaultMerger.merge(
      { ...args, current: currentWithoutSection },
      mergeFn
    )

    if (incoming.section) {
      merged.section = incoming.section
    }

    return merged
  },
}
